/**
 * ForgotPasswordPage — shadcn/ui redesign
 *
 * Visual changes (zero business-logic change):
 *   • All MUI imports removed; shadcn/ui + native HTML only
 *   • Consistent split-panel layout matching LoginPage
 *   • Two states: form view → success view (logic unchanged)
 *   • shadcn Alert variants: destructive (error), default (info), warning
 *   • Loader2 spinner inline on button
 *
 * API call unchanged: POST /password/request-reset
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { BrandLogo } from '../components/BrandLogo';

/** Shared left brand panel — identical to LoginPage */
const BrandPanel: React.FC = () => (
  <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground">
    <div className="flex items-center gap-2.5">
      <BrandLogo variant="mark" tone="light" className="h-9 w-9" />
      <span className="text-lg font-semibold tracking-tight">Converse</span>
    </div>
    <div className="space-y-3">
      <blockquote className="text-2xl font-semibold leading-snug">
        {/* "The support platform your team actually wants to use." */}
      </blockquote>
      <p className="text-sm text-primary-foreground/70">
        {/* Trusted by support teams across 10+ industries */}
      </p>
    </div>
    <p className="text-xs text-primary-foreground/50">
      © {new Date().getFullYear()} Dure Technologies Pvt Ltd
    </p>
  </div>
);

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/password/request-reset', { email });
      if (response.data.success) setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-svh grid lg:grid-cols-2">
      <BrandPanel />

      {/* ── Right: content panel ──────────────────────────────────────── */}
      <div className="flex items-center justify-center bg-background p-8 md:p-12">
        <div className="w-full max-w-sm space-y-6">

          {/* Mobile logo */}
          <div className="flex flex-col items-center gap-3 lg:hidden">
            <BrandLogo variant="full" showTagline={false} />
          </div>

          {!success ? (
            /* ── Form state ──────────────────────────────────────────── */
            <>
              {/* Icon + heading */}
              <div className="space-y-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start mb-3">
                  <div className="rounded-full bg-primary/10 p-3">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">Forgot password?</h1>
                <p className="text-sm text-muted-foreground">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              {/* Error */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading || !email}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Reset Link
                </Button>
              </form>

              {/* Back link */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to login
                </Link>
              </div>
            </>
          ) : (
            /* ── Success state ───────────────────────────────────────── */
            <>
              {/* Icon + heading */}
              <div className="space-y-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start mb-3">
                  <div className="rounded-full bg-emerald-100 p-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
                <p className="text-sm text-muted-foreground">
                  If an account exists for{' '}
                  <span className="font-medium text-foreground">{email}</span>
                  , we've sent a password reset link.
                </p>
              </div>

              {/* What's next */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>What's next?</AlertTitle>
                <AlertDescription>
                  <ol className="mt-1 space-y-1 list-decimal list-inside">
                    <li>Check your inbox (and spam folder)</li>
                    <li>Click the reset link in the email</li>
                    <li>Create your new password</li>
                    <li>Sign in with your new password</li>
                  </ol>
                </AlertDescription>
              </Alert>

              {/* Warning */}
              <Alert variant="warning">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  The reset link expires in <span className="font-medium">1 hour</span>.
                  Check your spam folder if you don't see the email.
                </AlertDescription>
              </Alert>

              {/* Actions */}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setSuccess(false); setEmail(''); }}
                >
                  Try another email
                </Button>
                <Button asChild className="flex-1">
                  <Link to="/login">Back to login</Link>
                </Button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
